<script setup>
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import LinkCellV2 from "@js/components/tables/cells/LinkCellV2.vue";
import NumberCell from "@js/components/tables/cells/NumberCell.vue";
import PillCell from "@js/components/tables/cells/PillCell.vue";
import StatusCellV2 from "@js/components/tables/cells/StatusCellV2.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";

const { tenant } = defineProps({
  tenant: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  count,
  globalFilter,
  filters,
  availableFilters,
  setGlobalFilter,
  setFilters,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "sourceUrl",
      header: () => t("label.sourceUrl"),
      cell: (props) =>
        h(LinkCellV2, {
          text: props.row.original.sourceUrl,
          href: props.row.original.sourceUrl,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "targetUrl",
      header: () => t("label.targetUrl"),
      cell: (props) =>
        props.row.original.targetUrl
          ? h(LinkCellV2, {
            text: props.row.original.targetUrl,
            href: props.row.original.targetUrl,
          })
          : null,
    }),
    columnHelper.accessor(() => null, {
      id: "urlInfo",
      header: () => t("label.finalUrl"),
      cell: (props) =>
        props.row.original.urlInfo
          ? h(LinkCellV2, {
            text: props.row.original.urlInfo,
            href: props.row.original.urlInfo,
          })
          : null,
    }),
    columnHelper.accessor(() => null, {
      id: "codeInfo",
      header: () => t("label.statusCode"),
      cell: (props) =>
        props.row.original.codeInfo
          ? h(PillCell, {
            pillValue: props.row.original.codeInfo,
          })
          : null,
    }),
    columnHelper.accessor(() => null, {
      id: "cmsId",
      header: () => t("label.cmsId"),
      cell: (props) =>
        props.row.original.cmsProductId || props.row.original.cmsCategoryId
          ? h(NumberCell, {
            number: props.row.original.cmsProductId
              ? props.row.original.cmsProductId
              : props.row.original.cmsCategoryId,
          })
          : null,
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "activeProduct",
      header: () => t("label.activeProduct"),
      cell: (props) =>
        props.row.original.cmsProductId
          ? h(StatusCellV2, {
            status: props.row.original.activeProduct,
          })
          : null,
    }),
    columnHelper.accessor(() => null, {
      id: "visibleProduct",
      header: () => t("label.visibleProduct"),
      cell: (props) =>
        props.row.original.cmsProductId
          ? h(StatusCellV2, {
            status: props.row.original.visibleProduct,
          })
          : null,
    }),
    columnHelper.accessor(() => null, {
      id: "activeCategory",
      header: () => t("label.activeCategory"),
      cell: (props) =>
        props.row.original.cmsCategoryId
          ? h(StatusCellV2, {
            status: props.row.original.activeCategory,
          })
          : null,
    }),
    columnHelper.accessor(() => null, {
      id: "discovered",
      header: () => t("label.discoveredAt"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.discovered,
        }),
    }),
  ],
  url: route("front.projectManagement.seo.postPermanentRedirectIssues", {
    tenant: tenant.id,
  }),
});
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <DataTableLayout
    v-else
    :table="table"
    :count="count"
    :global-filter="globalFilter"
    :filters="filters"
    :available-filters="availableFilters"
    :set-filters="setFilters"
    :set-global-filter="setGlobalFilter"
    wrap-headers
  />
</template>
